import { EditorState, Plugin } from 'hubspot-prosemirror/pm/state';
import { Slice } from 'hubspot-prosemirror/pm/model';
import parseHTML from 'rich-text-lib/utils/parseHTML';
import {
  convertToPlaintextState,
  PlaintextOptions,
  removeNonPlaintextNodes,
} from '../utils/plaintextUtils';
import { linkify, LinkifyOptions } from './link/linkify';

/** Configuration for paste behavior overrides */
export type PasteOverrideOptions = {
  /** When true, removes images from pasted content */
  stripImages?: boolean;
  /** When true, removes hyperlinks from pasted content (keeps text) */
  stripLinks?: boolean;
  /** When true, removes text color formatting from pasted content */
  stripTextColor?: boolean;
  /** When true, removes background highlighting from pasted content */
  stripTextHighlights?: boolean;
  /** When true, normalizes font sizes in pasted content */
  stripTextSize?: boolean;
  /** When true, removes bold, italic, and other text styles from pasted content */
  stripTextStyle?: boolean;
};

type PasteOverrideConfig = PasteOverrideOptions & {
  usePlaintextMode?: boolean;
  plaintextOptions?: PlaintextOptions;
  linkifyOptions?: LinkifyOptions;
};

/**
 * TODO: move this into state for the PasteOverridePlugin
 */
// eslint-disable-next-line no-restricted-syntax
let pasteOverrideConfig: PasteOverrideConfig;
type PasteTransformer = (htmlEl: HTMLElement) => HTMLElement;

/**
 * Create a transformer which removes a given CSS rule from any children
 * that have a style attribute.
 *
 * @param styleProperty CSS property to remove from all DOM nodes
 * @returns {PasteTransformer}
 */
const styleTransformer = (styleProperty: string) => {
  return (htmlEl: HTMLElement) => {
    if (htmlEl.querySelectorAll('[data-pm-slice]').length === 0) {
      htmlEl
        .querySelectorAll('[style]')
        .forEach((el) =>
          (el as HTMLElement).style.removeProperty(styleProperty)
        );
    }
    return htmlEl;
  };
};

/**
 * Create a transformer which removes all instances of an HTML element from
 * the children of a DOM node.
 *
 * @param tag Name of an HTML tag to remove
 * @returns {PasteTransformer}
 */
const removeTagTransformer = (tag: keyof HTMLElementTagNameMap) => {
  return (htmlEl: HTMLElement) => {
    if (htmlEl.querySelectorAll('[data-pm-slice]').length === 0) {
      const collection = htmlEl.getElementsByTagName(tag);
      while (collection.length > 0) {
        collection[0].remove();
      }
    }
    return htmlEl;
  };
};

/**
 * Create a transformer which replaces all instances of an HTML element with
 * their text content as a text node.
 *
 * @param tag Name of an HTML tag to replace with text nodes
 * @returns {PasteTransformer}
 */
const tagTextTransformer = (tag: keyof HTMLElementTagNameMap) => {
  return (htmlEl: HTMLElement) => {
    if (htmlEl.querySelectorAll('[data-pm-slice]').length === 0) {
      const collection = htmlEl.getElementsByTagName(tag);
      while (collection.length > 0) {
        collection[0].replaceWith(
          document.createTextNode(collection[0].innerText)
        );
      }
    }
    return htmlEl;
  };
};

const pasteTransformers: Record<string, PasteTransformer> = {
  ['stripTextHighlights']: styleTransformer('background-color'),
  ['stripTextColor']: styleTransformer('color'),
  ['stripTextStyle']: styleTransformer('font-family'),
  ['stripTextSize']: styleTransformer('font-size'),
  ['stripImages']: removeTagTransformer('img'),
  ['stripLinks']: tagTextTransformer('a'),
};

export const PasteOverridePlugin = new Plugin({
  state: {
    init() {
      return {};
    },
    apply(tr, currentPluginState) {
      const meta = tr.getMeta(PasteOverridePlugin);
      if (meta) {
        pasteOverrideConfig = meta.pasteOverrideConfig || pasteOverrideConfig;
        return meta;
      }
      return currentPluginState;
    },
  },
  props: {
    transformPastedHTML: (html) => {
      if (!pasteOverrideConfig) return html;

      let htmlEl = parseHTML(html);
      for (const prop in pasteOverrideConfig) {
        if (
          (pasteOverrideConfig as Record<string, any>)[prop] &&
          pasteTransformers[prop]
        ) {
          htmlEl = pasteTransformers[prop](htmlEl);
        }
      }

      return htmlEl.outerHTML;
    },
    handlePaste: (view, _event, slice) => {
      if (pasteOverrideConfig?.usePlaintextMode) {
        let sliceState = EditorState.create({
          schema: view.state.schema,
        });
        sliceState = sliceState.apply(
          sliceState.tr.replace(0, sliceState.doc.content.size, slice)
        );

        const doc = removeNonPlaintextNodes(
          convertToPlaintextState(
            sliceState,
            pasteOverrideConfig.plaintextOptions,
            true
          )
        ).doc;

        const plainSlice = doc.slice(1, doc.content.size - 1);
        const finalSlice = pasteOverrideConfig.linkifyOptions
          ? new Slice(
              linkify(plainSlice.content, pasteOverrideConfig.linkifyOptions),
              plainSlice.openStart,
              plainSlice.openEnd
            )
          : plainSlice;

        view.dispatch(view.state.tr.replaceSelection(finalSlice));
        return true;
      }
      return false;
    },
  },
});
